import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { RealEstateCatalogResponse } from '../../models/apiTypes';
import { getRealEstateName } from '../../data/realEstate';
import ScrollHint from '../realEstate/ScrollHint';
import { getRealEstateCatalogRealEstateGet } from '../../client';
import { REAL_ESTATE_FIXED_SIZES } from '../../scenes/map/mapMarkerSizes';
import ModalFrame from '../primitives/ModalFrame';
import posthog from 'posthog-js';
import RandomBox from '../primitives/RandomBox';
import Confetti from 'react-confetti';
import useSound from 'use-sound';
import useSoundStore from '../../stores/useSoundStore';
import InsufficientPearlsModal, { InsufficientCurrencyError } from './InsufficientPearlsModal';
import PearlsBalance from '../../components/PearlsBalance';
import RealEstateCard from '../realEstate/RealEstateCard';
import RealEstateQuotaModal from '../realEstate/RealEstateQuotaModal';
import RealEstatePurchaseSuccessModal from '../realEstate/RealEstatePurchaseSuccessModal';
import { cn } from '../cn';
import { buildOnboardingLayoutEntries, buildRealEstateCatalog, buildRealEstateLayoutEntries } from '../realEstate/realEstateCatalog';
import {
  handleBuyWithGameCurrency as runBuyWithGameCurrency,
  handleBuyWithStars as runBuyWithStars,
  type RealEstatePurchaseDeps,
  type RealEstateQuotaExceededError,
} from '../realEstate/realEstatePurchase';


interface RealEstateModalProps {
  isOpen: boolean;
  plotId: number | null;
  onClose: () => void;
  /** MapCanvas: наближення до щойно купленого будинку (без Zustand). */
  onNavigateToPurchasedHouse?: (mapObjectId: number) => void;
  /** Чи відкрито під час онбордінгу (glow на кнопці Buy, без backdrop close). */
  isOnboarding?: boolean;
}

export default function RealEstateModal({
  isOpen,
  plotId,
  onClose,
  onNavigateToPurchasedHouse,
  isOnboarding = false,
}: RealEstateModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openTimeRef = useRef<number>(0);
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [insufficientCurrencyError, setInsufficientCurrencyError] = useState<InsufficientCurrencyError | null>(null);
  const [quotaExceededError, setQuotaExceededError] = useState<RealEstateQuotaExceededError | null>(null);
  const [errorRealEstateId, setErrorRealEstateId] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiSoundPlayedRef = useRef(false);
  const [showPurchaseSuccessModal, setShowPurchaseSuccessModal] = useState(false);
  const [purchasedRealEstateId, setPurchasedRealEstateId] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { soundsEnabled } = useSoundStore();
  const [playConfetti] = useSound('/game-assets/sounds/confetti.mp3', {
    volume: 0.12,
    soundEnabled: soundsEnabled,
  });

  // Завантажуємо дані при відкритті каталогу (якщо їх немає в кеші)
  const cachedCatalogData = queryClient.getQueryData<RealEstateCatalogResponse>(['realEstates']);

  // Завантажуємо дані при відкритті каталогу
  // Використовуємо initialData щоб показувати дані з кешу одразу (не блокуємо інтерфейс)
  // refetchOnMount: 'always' виконує запит в фоні при кожному відкритті каталогу
  const { data: catalogData, isLoading } = useQuery<RealEstateCatalogResponse>({
    queryKey: ['realEstates'],
    queryFn: async () => {
      const { data } = await getRealEstateCatalogRealEstateGet({ throwOnError: true });
      return data;
    },
    enabled: isOpen,
    initialData: cachedCatalogData, // Показуємо дані з кешу одразу, якщо є
    retry: 3,
    refetchOnMount: 'always', // Завжди виконуємо один запит при відкритті каталогу (в фоні)
    staleTime: 30000, // Дані вважаються свіжими 30 секунд (щоб не було занадто частих запитів)
  });

  // Не `?? []` тут: новий [] щорендер ламає memo deps (exhaustive-deps).
  const realEstates = catalogData?.items;
  const firstHouseDiscountAvailable = catalogData?.first_house_discount_available ?? false;

  // Use centralized functions from realEstate.ts
  const getRealEstateNameLocal = (id: number): string => {
    return getRealEstateName(id, t);
  };

  // (b) Clear error/confetti state when modal closes — during render
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setInsufficientCurrencyError(null);
      setQuotaExceededError(null);
      setErrorRealEstateId(null);
      setShowConfetti(false);
      // Success modal is handled separately — don't clear it here
    }
  }

  // Analytics only on open/close (external side effects — no setState)
  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
      posthog.capture('real_estate_catalog_opened', { plot_id: plotId, is_onboarding: isOnboarding });
    } else if (openTimeRef.current > 0) {
      const duration = Math.round((Date.now() - openTimeRef.current) / 1000);
      posthog.capture('real_estate_catalog_closed', { plot_id: plotId, duration_seconds: duration, is_onboarding: isOnboarding });
    }
    // Тригер лише isOpen: open/close analytics.
    // plotId / isOnboarding — snapshot у момент open/close; зміна mid-open не повторює events.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/close only
  }, [isOpen]);

  // Відтворюємо звук конфеті одноразово, коли вони з'являються
  useEffect(() => {
    if (showConfetti && !confettiSoundPlayedRef.current) {
      playConfetti();
      confettiSoundPlayedRef.current = true;
    }
    // Скидаємо прапорець, коли конфеті зникають
    if (!showConfetti) {
      confettiSoundPlayedRef.current = false;
    }
  }, [showConfetti, playConfetti]);

  // Усе, що обробники покупки читали з рендер-скоупу
  const purchaseDeps: RealEstatePurchaseDeps = {
    plotId,
    realEstates: realEstates ?? [],
    queryClient,
    t,
    getRealEstateNameLocal,
    isProcessing,
    setIsProcessing,
    setInsufficientCurrencyError,
    setQuotaExceededError,
    setErrorRealEstateId,
    setShowConfetti,
    setPurchasedRealEstateId,
    setShowPurchaseSuccessModal,
    onClose,
  };

  const handleBuyWithGameCurrency = (realEstateId: number) => {
    void runBuyWithGameCurrency(purchaseDeps, realEstateId);
  };

  const handleBuyWithStars = (realEstateId: number) => {
    void runBuyWithStars(purchaseDeps, realEstateId);
  };

  const catalog = useMemo(
    () => buildRealEstateCatalog(realEstates ?? [], firstHouseDiscountAvailable, t),
    [realEstates, firstHouseDiscountAvailable, t],
  );

  // Onboarding: the list peeks down and back, so the player sees there are more houses.

  // Onboarding shows only what the player can buy now — the focus is the purchase.
  const layoutEntries = useMemo(
    () => (isOnboarding ? buildOnboardingLayoutEntries(catalog) : buildRealEstateLayoutEntries(catalog)),
    [catalog, isOnboarding],
  );


  if (!isOpen) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        className="fixed inset-0 z-[2000] flex items-center justify-center bg-scrim"
        onClick={onClose}
      >
        <div className="text-[18px] text-white">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <>
      {showConfetti && (
        <div className="pointer-events-none fixed inset-0 z-[10003]">
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
          />
        </div>
      )}
      <ModalFrame
        isOpen={isOpen && !showPurchaseSuccessModal}
        onClose={onClose}
        title={t("Choose your cozy virtual house now!!!")}
      showBackButton={true}
      closeOnBackdropClick={false}
      scrollContainerRef={scrollContainerRef}
      backdropClassName={cn(
        'modal-frame-bottom-gradient [--modal-frame-gradient-max-width:520px]',
        isOnboarding && 'bg-black-alpha-90', // the map behind fades out: only the houses matter
      )}
      renderHeader={() => (
          <header className="sticky top-0 z-[2010] flex flex-col items-center bg-[image:var(--gradient-modal-header)] pt-2xl pb-xl">
            <div className="flex w-full items-center justify-between">
              <div className="h-[36px] w-[36px] shrink-0" />
              <p className="m-0 max-w-[280px] text-center text-[14px] font-normal text-white">
                {t("Choose your cozy virtual house now!!!")}
              </p>
              <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className={cn(
                  'm-0 h-7 w-7 shrink-0 cursor-pointer border-none bg-transparent p-0 text-center leading-7',
                  // Onboarding: still there, but quiet — the buy buttons lead.
                  isOnboarding ? 'text-[14px] text-white-alpha-20' : 'text-[18px] text-white-alpha-50',
                )}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mt-[10px] rounded-pill border-[1.5px] border-solid border-reward-alpha-35 bg-[linear-gradient(135deg,var(--color-accent-alpha-15)_0%,var(--color-reward-alpha-8)_100%)] px-[18px] py-md">
              <PearlsBalance
                variant="dark"
                compact
                padding="0"
                style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-white-alpha-70)' }}
                valueStyle={{ fontWeight: 700, fontSize: 17, color: 'var(--color-gold)' }}
              />
            </div>
          </header>
        )}
      >
        <div className="flex flex-col gap-xl pb-md">
          {layoutEntries.map((entry, index) => {
            if (entry.kind === 'box') {
              return (
                <RandomBox
                  key={`box-${entry.config.houseIds.join('-')}`}
                  plotId={plotId}
                  onBuy={handleBuyWithStars}
                  isProcessing={isProcessing}
                  getRealEstateName={getRealEstateNameLocal}
                  config={entry.config}
                />
              );
            }
            const item = entry.item;
            return (
              <RealEstateCard
                key={`house-${item.id}-${index}`}
                item={item}
                plotId={plotId}
                isProcessing={isProcessing}
                isOnboarding={isOnboarding}
                onBuyWithPearls={handleBuyWithGameCurrency}
                onBuyWithStars={handleBuyWithStars}
              />
            );
          })}
        </div>
      </ModalFrame>
      <ScrollHint scrollRef={scrollContainerRef} active={isOnboarding && isOpen && !isLoading && !showPurchaseSuccessModal} />

      <InsufficientPearlsModal
        error={insufficientCurrencyError && errorRealEstateId ? insufficientCurrencyError : null}
        onClose={() => {
          setInsufficientCurrencyError(null);
          setErrorRealEstateId(null);
        }}
        requiredLabel={t("To purchase this house, you need:")}
        onGetPearls={() => {
          setInsufficientCurrencyError(null);
          setErrorRealEstateId(null);
          onClose();
          navigate('/shop?category=pearls');
        }}
        preview={errorRealEstateId ? (() => {
          const houseItem = catalog.find((item) => item.id === errorRealEstateId);
          if (!houseItem) return undefined;
          const fixedSize = REAL_ESTATE_FIXED_SIZES[errorRealEstateId];
          return (
            <div className="mb-xl flex justify-center">
              <div className="inline-flex items-center justify-center rounded-xl border-2 border-solid border-daily-quest-auxiliary-modals-border-2 bg-[url('/ui/tap-screen/house-background.webp')] bg-[length:auto_100%] bg-center bg-no-repeat px-2xl py-[13px]">
                <img
                  src={houseItem.image}
                  alt={houseItem.name}
                  className="block object-contain"
                  style={{
                    width: fixedSize ? `${fixedSize.width}px` : 'auto',
                    height: fixedSize ? `${fixedSize.height}px` : 'auto',
                  }}
                />
              </div>
            </div>
          );
        })() : undefined}
      />

      {/* Попап з помилкою досягнення квоти */}
      {quotaExceededError && errorRealEstateId && (
        <RealEstateQuotaModal
          houseItem={catalog.find(item => item.id === errorRealEstateId)}
          onClose={() => {
            setQuotaExceededError(null);
            setErrorRealEstateId(null);
          }}
          onLearnMore={() => {
            setQuotaExceededError(null);
            setErrorRealEstateId(null);
            onClose();
            navigate('/shop?category=vip');
          }}
        />
      )}

      {/* Попап після успішної покупки будинку */}
      {showPurchaseSuccessModal && purchasedRealEstateId && (
        <RealEstatePurchaseSuccessModal
          purchasedRealEstateId={purchasedRealEstateId}
          onGoToHouse={() => {
            setShowPurchaseSuccessModal(false);
            const targetId = plotId;
            onClose();
            if (targetId != null && onNavigateToPurchasedHouse) {
              window.setTimeout(() => onNavigateToPurchasedHouse(targetId), 200);
            }
          }}
        />
      )}
    </>
  );
}
